import { ofetch, FetchError } from 'ofetch';
import { ApiClientError } from '../api/errors';
import { createDebugLogger } from '../utils/debug';
import { attachBearer } from '../auth/attachBearer';

const dlog = createDebugLogger('nexus');

/**
 * Nexus-API-Client.
 *
 * Geht IMMER über den BFF-Proxy `/proxy/nexus/...` — Frontend sieht das
 * M2M-Secret nie. Production: server.js routet weiter zu NEXUS_API_URL
 * mit `Authorization: M2M <secret>`-Header.
 *
 * Brand-Origin wird NICHT gesetzt (Nexus ist tenantless für /v1/admin/*).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/INBOX.md — Auth-Sektion.
 */

function buildRaw(baseURL) {
  return ofetch.create({
    baseURL,
    onRequest(ctx) {
      attachBearer(ctx); // #501: Operator-Bearer für die Server-Verifikation
      dlog(`→ ${baseURL}`, ctx.request);
    },
    onResponse({ request, response }) {
      dlog(`← ${baseURL}`, request, response.status);
    },
  });
}

const rawM2m = buildRaw('/proxy/nexus');
const rawBearer = buildRaw('/proxy/nexus-bearer');

async function execWithMapping(rawFn, path, opts) {
  try {
    return await rawFn(path, opts);
  } catch (err) {
    if (err instanceof FetchError) {
      const data = err.data;
      const code = data?.error?.code ?? 'nexus_request_failed';
      const message = data?.error?.message ?? err.message;
      throw new ApiClientError({
        code,
        message,
        status: err.response?.status ?? 0,
        details: data?.error?.details,
      });
    }
    throw err;
  }
}

/** Server-side M2M-Auth — für /v1/admin/* */
export async function nexusFetch(path, opts = {}) {
  return execWithMapping(rawM2m, path, opts);
}

/**
 * Forward des Operator-Bearer-Headers — für /v1/jobs/* die per
 * Operator-Token aus dem CORE-Login-Flow auth'd werden. Auth-Provider
 * wird hier nicht direkt benutzt — ofetch nutzt den Standard-fetch der
 * Browser, Browser schickt aktuell aber keinen Authorization-Header von
 * sich aus. Caller muss `Authorization` explizit setzen, z.B. via
 * useNexusBearerFetch-Hook (siehe pages/NexusJobs).
 */
export async function nexusBearerFetch(path, opts = {}) {
  return execWithMapping(rawBearer, path, opts);
}

export function isNexusProxyEnabled() {
  if (typeof window === 'undefined') return false;
  return window.__ORBIT_CONFIG__?.nexusProxyEnabled === true;
}
